"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUnscheduledQueue } from "@/hooks/use-calendar";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, AlertTriangle, Upload, CheckCircle2 } from "lucide-react";
import type { UnscheduledItem } from "@content-os/types";

interface UnscheduledQueueProps {
  brandId: string;
  onItemClick: (item: UnscheduledItem) => void;
  onDragStart?: (item: UnscheduledItem, e: React.DragEvent) => void;
}

export function UnscheduledQueue({ brandId, onItemClick, onDragStart }: UnscheduledQueueProps) {
  const { t } = useTranslation("calendar");
  const { data: items = [] } = useUnscheduledQueue(brandId);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    approved: true,
    uploaded: true,
    failed: true,
  });

  const approved = items.filter((i) => i.source === "approved");
  const uploaded = items.filter((i) => i.source === "uploaded");
  const failed = items.filter((i) => i.source === "failed");

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="w-56 border-r bg-card flex flex-col overflow-hidden">
      <div className="px-3 py-2 border-b">
        <h3 className="text-sm font-semibold">{t("queue.title")}</h3>
        <p className="text-xs text-muted-foreground">{t("queue.items", { count: items.length })}</p>
      </div>

      <div className="flex-1 overflow-y-auto">
        <QueueSection
          title={t("queue.approved")}
          icon={<CheckCircle2 size={12} className="text-green-500" />}
          items={approved}
          expanded={expandedSections.approved ?? true}
          onToggle={() => toggleSection("approved")}
          onItemClick={onItemClick}
          onDragStart={onDragStart}
          videoLabel={t("queue.video")}
          imageLabel={t("queue.image")}
        />
        <QueueSection
          title={t("queue.uploaded")}
          icon={<Upload size={12} className="text-blue-500" />}
          items={uploaded}
          expanded={expandedSections.uploaded ?? true}
          onToggle={() => toggleSection("uploaded")}
          onItemClick={onItemClick}
          onDragStart={onDragStart}
          videoLabel={t("queue.video")}
          imageLabel={t("queue.image")}
        />
        <QueueSection
          title={t("queue.failed")}
          icon={<AlertTriangle size={12} className="text-red-500" />}
          items={failed}
          expanded={expandedSections.failed ?? true}
          onToggle={() => toggleSection("failed")}
          onItemClick={onItemClick}
          onDragStart={onDragStart}
          videoLabel={t("queue.video")}
          imageLabel={t("queue.image")}
        />
      </div>
    </div>
  );
}

function QueueSection({
  title, icon, items, expanded, onToggle, onItemClick, onDragStart, videoLabel, imageLabel,
}: {
  title: string;
  icon: React.ReactNode;
  items: UnscheduledItem[];
  expanded: boolean;
  onToggle: () => void;
  onItemClick: (item: UnscheduledItem) => void;
  onDragStart?: (item: UnscheduledItem, e: React.DragEvent) => void;
  videoLabel: string;
  imageLabel: string;
}) {
  if (items.length === 0) return null;

  return (
    <div className="border-b last:border-b-0">
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 w-full px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-accent/50"
      >
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {icon}
        {title}
        <Badge variant="secondary" className="ml-auto text-[10px] h-4 px-1">{items.length}</Badge>
      </button>
      {expanded && (
        <div className="px-2 pb-2 space-y-1">
          {items.map((item) => (
            <div
              key={item.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData("application/json", JSON.stringify(item));
                onDragStart?.(item, e);
              }}
              onClick={() => onItemClick(item)}
              className="flex items-center gap-2 rounded-md p-1.5 hover:bg-accent/50 cursor-grab active:cursor-grabbing"
            >
              {item.thumbnailUrl ? (
                <img src={item.thumbnailUrl} alt="" className="w-8 h-8 rounded object-cover" />
              ) : (
                <div className="w-8 h-8 rounded bg-muted flex items-center justify-center text-xs">
                  {item.mediaType === "video" ? videoLabel : imageLabel}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono truncate">{item.title}</div>
                {item.source === "failed" && (
                  <span className="text-[10px] text-red-400">
                    {item.failedPlatforms?.join(", ")}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
